import { MinusIcon, PlusIcon } from '@radix-ui/react-icons';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Slider,
  Text,
  TextField,
} from '@radix-ui/themes';
import { CommonInputHint } from 'components/common/form/hint';
import { CommonInputLabel } from 'components/common/form/label';
import { CommonInputWrapper } from 'components/common/form/wrapper';
import { t } from 'i18next';
import { ISliderInput } from 'interfaces/forms/slider';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { isNil } from 'lodash';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

/**
 * Default variant: Simple slider with optional stepper buttons above.
 */
const DefaultSlider = (props: ISliderInput) => {
  if (isNil(props.value)) {
    return null;
  }

  return (
    <CommonInputWrapper {...props}>
      <CommonInputLabel {...props} />

      {props.showStepper && (
        <Flex gap={RADIX.FLEX.GAP.SM} justify="center">
          <Box>
            <IconButton
              color={RADIX.COLOR.NEUTRAL}
              variant="soft"
              disabled={props.disabled || props.value <= props.min}
              onClick={() => {
                if (isNil(props.value)) {
                  return;
                }

                const next = props.value - props.step;
                props.onChange?.(next);
              }}
            >
              <MinusIcon />
            </IconButton>
          </Box>

          {props.stepperLabel && (
            <Box flexGrow="1">
              <Button
                color={RADIX.COLOR.NEUTRAL}
                variant="soft"
                disabled={props.disabled}
                className="btn-block"
              >
                {t(props.stepperLabel)}:{' '}
                {props.value.toFixed(props.stepperDecimals ?? 0)}
              </Button>
            </Box>
          )}

          <Box>
            <IconButton
              color={RADIX.COLOR.NEUTRAL}
              variant="soft"
              disabled={props.disabled || props.value >= props.max}
              onClick={() => {
                if (isNil(props.value)) {
                  return;
                }

                const next = props.value + props.step;
                props.onChange?.(next);
              }}
            >
              <PlusIcon />
            </IconButton>
          </Box>
        </Flex>
      )}

      <Slider
        color={props.inputColor}
        min={props.min}
        max={props.max}
        step={props.step}
        value={[props.value]}
        disabled={props.disabled}
        onValueChange={(v) => {
          props.onChange?.(v[0]);
        }}
      />

      <CommonInputHint {...props} />
    </CommonInputWrapper>
  );
};

/**
 * With-input variant: Synchronized slider + numeric input with side buttons.
 */
const WithInputSlider = (props: ISliderInput) => {
  const {
    value,
    min,
    max,
    step,
    disabled = false,
    onChange,
    label,
    labelIcon,
    units = '',
    decimals = 2,
    showCurrent = true,
    showButtons = true,
    customCurrent,
  } = props;

  const id = useId();

  // Track whether the input is currently focused to avoid sync conflicts
  const isFocusedRef = useRef(false);

  // Local input state to allow temporary invalid values while typing
  const [inputValue, setInputValue] = useState<string>(
    value?.toFixed(decimals) ?? ''
  );

  // Sync inputValue when external value changes, but only if input is NOT focused
  useEffect(() => {
    if (isFocusedRef.current || isNil(value)) {
      return;
    }
    setInputValue(value.toFixed(decimals));
  }, [value, decimals]);

  useEffect(() => {
    if (!disabled) {
      return;
    }
    // onBlur won't fire if the component becomes disabled while focused, so we need to reset here
    isFocusedRef.current = false;
  }, [disabled]);

  /**
   * Clamps a value to [min, max] and snaps to nearest step.
   */
  const clampAndSnap = useCallback(
    (rawValue: number): number => {
      const clamped = Math.max(min, Math.min(max, rawValue));
      const snapped = Math.round(clamped / step) * step;
      return parseFloat(snapped.toFixed(decimals));
    },
    [min, max, step, decimals]
  );

  /**
   * Called when the text input changes.
   */
  const handleInputChange = useCallback(
    (newInputValue: string) => {
      setInputValue(newInputValue);

      const parsed = parseFloat(newInputValue);
      if (!isNaN(parsed)) {
        const snapped = clampAndSnap(parsed);
        onChange?.(snapped);
      }
    },
    [clampAndSnap, onChange]
  );

  /**
   * Called on blur or Enter: coerce to number, clamp, snap, and update.
   */
  const handleInputCommit = useCallback(() => {
    const parsed = parseFloat(inputValue);
    const finalValue = isNaN(parsed) ? (value ?? min) : clampAndSnap(parsed);
    onChange?.(finalValue);
    setInputValue(finalValue.toFixed(decimals));
  }, [inputValue, value, min, clampAndSnap, onChange, decimals]);

  /**
   * Called when the slider value changes.
   */
  const handleSliderChange = useCallback(
    (values: number[]) => {
      const rawValue = values[0];
      const snapped = clampAndSnap(rawValue);
      onChange?.(snapped);
    },
    [clampAndSnap, onChange]
  );

  const handleDecrement = useCallback(() => {
    if (isNil(value)) {
      return;
    }
    const next = clampAndSnap(value - step);
    onChange?.(next);
  }, [value, step, clampAndSnap, onChange]);

  const handleIncrement = useCallback(() => {
    if (isNil(value)) {
      return;
    }
    const next = clampAndSnap(value + step);
    onChange?.(next);
  }, [value, step, clampAndSnap, onChange]);

  if (isNil(value)) {
    return null;
  }

  const canDecrement = value > min;
  const canIncrement = value < max;

  return (
    <CommonInputWrapper {...props}>
      <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
        {/* Label row: label on left, input + units on right */}
        <Flex justify="between" align="center">
          <Flex align="center" gap={RADIX.FLEX.GAP.XS}>
            <Text as="label" htmlFor={`${id}-input`} weight="medium">
              {label ? t(label).toString() : ''}
            </Text>
            {labelIcon}
          </Flex>

          <TextField.Root
            id={`${id}-input`}
            size="1"
            type="text"
            inputMode="decimal"
            value={inputValue}
            disabled={disabled}
            color={props.inputColor}
            onFocus={() => {
              isFocusedRef.current = true;
            }}
            onChange={(e) => handleInputChange(e.target.value)}
            onBlur={() => {
              isFocusedRef.current = false;
              handleInputCommit();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleInputCommit();
                (e.target as HTMLInputElement).blur();
                return;
              }

              // Arrow key support: Shift = ±1, otherwise ±0.1
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault();
                const delta = e.shiftKey ? 1 : 0.1;
                const direction = e.key === 'ArrowUp' ? 1 : -1;
                const next = clampAndSnap(value + delta * direction);
                onChange?.(next);
                setInputValue(next.toFixed(decimals));
              }
            }}
            onWheel={(e) => {
              (e.target as HTMLElement).blur();
            }}
            style={{
              width: '100px',
              textAlign: 'right',
            }}
          >
            <TextField.Slot side="left">
              <span style={{ width: 'var(--space-1)' }} />
            </TextField.Slot>
            {units && (
              <TextField.Slot side="right">
                <Text
                  size="1"
                  color="gray"
                  style={{ marginRight: 'var(--space-1)' }}
                >
                  {units}
                </Text>
              </TextField.Slot>
            )}
          </TextField.Root>
        </Flex>

        {/* Slider row: optional minus button, slider, optional plus button */}
        <Flex align="center" gap={RADIX.FLEX.GAP.SM}>
          {showButtons && (
            <IconButton
              size="1"
              variant="soft"
              color={RADIX.COLOR.NEUTRAL}
              disabled={disabled || !canDecrement}
              onClick={handleDecrement}
              aria-label={`Decrease ${label ?? ''}`}
            >
              <MinusIcon />
            </IconButton>
          )}

          <Flex flexGrow="1">
            <Slider
              size="1"
              color={props.inputColor}
              min={min}
              max={max}
              step={step}
              value={[value]}
              disabled={disabled}
              onValueChange={handleSliderChange}
              aria-label={label ?? ''}
              style={{ width: '100%' }}
            />
          </Flex>

          {showButtons && (
            <IconButton
              size="1"
              variant="soft"
              color={RADIX.COLOR.NEUTRAL}
              disabled={disabled || !canIncrement}
              onClick={handleIncrement}
              aria-label={`Increase ${label ?? ''}`}
            >
              <PlusIcon />
            </IconButton>
          )}
        </Flex>

        {/* Current value helper text */}
        {showCurrent && (
          <Text size="1" color="gray">
            Current: {(customCurrent ?? value).toFixed(decimals)} {units} | Min:{' '}
            {min} · Max: {max}
          </Text>
        )}
      </Flex>

      <CommonInputHint {...props} />
    </CommonInputWrapper>
  );
};

/**
 * Common slider input component with two variants:
 * - 'default': Simple slider with optional stepper buttons above
 * - 'with-input': Synchronized slider + numeric input with side buttons
 */
export const CommonSliderInput = (props: ISliderInput) => {
  if (props.withInput) {
    return <WithInputSlider {...props} />;
  }

  return <DefaultSlider {...props} />;
};
